import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useClinic } from "@/context/ClinicContext";
import { Skeleton } from "@/components/Skeleton";
import { locationsFromSlots, filterSlotsByLocation } from "@/lib/locations";
import { Check, Loader2, Search, X } from "lucide-react";

/** Staff booking drawer — patient, visit type, time, confirm.
 *
 *  Shared by /schedule (click an open slot) and the patient profile (book on behalf),
 *  because they are the same transaction with a different starting point: the calendar
 *  knows the time and needs a patient, the profile knows the patient and needs a time.
 *  Two copies of a booking form would drift, and the one that drifts is the one that
 *  forgets to send location_id.
 */

function timeLabel(startUtc, tz) {
  return new Date(startUtc).toLocaleTimeString(undefined, {
    hour: "numeric", minute: "2-digit", timeZone: tz,
  });
}

function dayLabel(iso) {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: "long", month: "short", day: "numeric", timeZone: "UTC",
  });
}

export default function BookingDrawer({
  open, onClose, onBooked,
  patient: lockedPatient = null,   // booking on behalf: patient is fixed
  providerId = "",                 // prefill from a clicked open slot
  startUtc = "",
  visitTypeId = "",
  date = "",
  locationId = "",                 // the schedule's location filter, if one is set
}) {
  const { clinic } = useClinic();
  const tz = clinic?.timezone || "America/New_York";

  // ClinicContext carries only the public branding allowlist, and the override policy is
  // deliberately not in it — so ask the staff scheduling endpoint rather than offering an
  // override this clinic has switched off and letting the backend 403 the click.
  const [allowOverride, setAllowOverride] = useState(true);
  const [visitTypes, setVisitTypes] = useState(null);
  const [vtId, setVtId] = useState(visitTypeId);
  const [day, setDay] = useState(date);
  const [slots, setSlots] = useState(null);
  const [chosen, setChosen] = useState(null);      // the slot object we will book
  const [note, setNote] = useState("");

  // Which site, when the choice is real. Derived from the slots themselves rather than
  // the clinic's location list: a two-site clinic may still run IV therapy at only one.
  const [locChoice, setLocChoice] = useState(locationId);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(lockedPatient);

  // Depend on the id/name, never the object: callers build `patient={{id, name}}` inline,
  // so a new reference arrives on every render and an object dependency would re-fire the
  // reset effect forever.
  const lockedId = lockedPatient?.id || "";
  const lockedName = lockedPatient?.name || "";

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);  // a 409 we could override
  const [override, setOverride] = useState("");

  // Re-prime whenever the drawer is opened from a new starting point.
  useEffect(() => {
    if (!open) return;
    setVtId(visitTypeId);
    setDay(date || new Date().toLocaleDateString("en-CA", { timeZone: tz }));
    setChosen(startUtc ? { start_utc: startUtc, provider_id: providerId } : null);
    setNote(""); setError(""); setConflict(false); setOverride(""); setLocChoice(locationId);
    setPatient(lockedId ? { id: lockedId, name: lockedName } : null);
    setQuery(""); setResults([]);
  }, [open, visitTypeId, date, startUtc, providerId, locationId, lockedId, lockedName, tz]);

  useEffect(() => {
    if (!open || visitTypes) return;
    api.get("/visit-types")
      .then((r) => {
        setVisitTypes(r.data);
        setVtId((cur) => cur || r.data[0]?.id || "");
      })
      .catch((e) => { setVisitTypes([]); setError(formatApiError(e.response?.data?.detail)); });
    api.get("/clinic/scheduling")
      .then((r) => setAllowOverride(r.data.allow_double_booking_override !== false))
      .catch(() => {});
  }, [open, visitTypes]);

  // Slots for the chosen visit type + day. The prefilled slot came from the same engine,
  // so re-running it just re-renders what the caller already saw — and keeps the list
  // honest if the visit type is changed in here (a 60-minute visit does not fit every
  // 20-minute opening).
  const loadSlots = useCallback(() => {
    if (!open || !vtId || !day) return;
    setSlots(null);
    const params = { visit_type_id: vtId, from: day, to: day };
    if (providerId) params.provider_id = providerId;
    api.get("/availability", { params })
      .then((r) => {
        const list = r.data.slots || [];
        setSlots(list);
        // Keep the prefilled pick only if it is still on offer for this visit type.
        setChosen((cur) => (cur ? list.find((s) => s.start_utc === cur.start_utc
                                                && s.provider_id === cur.provider_id) || null : null));
      })
      .catch((e) => { setSlots([]); setError(formatApiError(e.response?.data?.detail)); });
  }, [open, vtId, day, providerId]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  // Patient search — skipped entirely when the caller already fixed the patient.
  useEffect(() => {
    if (lockedId || !open) return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/patients", { params: { search: q } })
        .then((r) => setResults(r.data.slice(0, 8)))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, lockedId, open]);

  const vt = useMemo(
    () => (visitTypes || []).find((v) => v.id === vtId) || null,
    [visitTypes, vtId]);

  const slotLocations = useMemo(() => locationsFromSlots(slots), [slots]);
  // Ask only when the answer could differ. One site in play resolves silently — a
  // dropdown with a single option is a decision that isn't one.
  const showLocationSelect = slotLocations.length >= 2;
  const shownSlots = useMemo(
    () => filterSlotsByLocation(slots || [], locChoice), [slots, locChoice]);

  // Only label locations when more than one is on offer AND nothing has been picked —
  // once you have chosen a site, repeating it on every chip is noise.
  const multiLocation = showLocationSelect && !locChoice;

  async function confirm() {
    if (!patient || !chosen || !vtId) return;
    setBusy(true); setError("");
    try {
      const body = {
        patient_id: patient.id,
        visit_type_id: vtId,
        provider_id: chosen.provider_id,
        location_id: chosen.location_id,
        start_utc: chosen.start_utc,
        note,
      };
      if (conflict && override.trim()) body.override_reason = override.trim();
      const r = await api.post("/appointments", body);
      onBooked?.(r.data);
      onClose?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // A lost race hands back the nearest open times; render those rather than making
      // front desk go and hunt the grid again.
      if (detail && typeof detail === "object" && detail.alternatives) {
        setError(detail.message);
        setSlots(detail.alternatives);
        setChosen(null);
        setConflict(true);
      } else {
        setError(formatApiError(detail));
      }
    } finally { setBusy(false); }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="booking-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-card w-full max-w-md h-full overflow-y-auto border-l border-border shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <h3 className="font-heading text-lg font-semibold">Book a visit</h3>
          <button onClick={onClose} data-testid="booking-drawer-close"
                  className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Patient */}
          <div>
            <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Patient</span>
            {lockedPatient ? (
              <p className="mt-1.5 text-sm font-medium" data-testid="booking-patient-locked">
                {lockedPatient.name}
              </p>
            ) : patient ? (
              <div className="mt-1.5 flex items-center justify-between gap-2 border border-border rounded-md px-3 py-2"
                   data-testid="booking-patient-chosen">
                <span className="text-sm font-medium">{patient.name}</span>
                <button onClick={() => { setPatient(null); setQuery(""); }}
                        className="text-xs text-muted-foreground hover:text-foreground">Change</button>
              </div>
            ) : (
              <div className="mt-1.5">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input data-testid="booking-patient-search" value={query} autoFocus
                         onChange={(e) => setQuery(e.target.value)}
                         placeholder="Search by name or email…"
                         className="w-full border border-input bg-card rounded-md pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
                </div>
                {results.length > 0 && (
                  <div className="mt-2 border border-border rounded-md divide-y divide-border overflow-hidden"
                       data-testid="booking-patient-results">
                    {results.map((p) => (
                      <button key={p.id} data-testid={`booking-patient-${p.id}`}
                              onClick={() => setPatient({ id: p.id, name: `${p.first_name} ${p.last_name}` })}
                              className="w-full text-left px-3 py-2 hover:bg-secondary">
                        <span className="text-sm font-medium">{p.first_name} {p.last_name}</span>
                        <span className="block text-xs text-muted-foreground">{p.email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Visit type */}
          <label className="block">
            <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Visit type</span>
            {visitTypes === null ? <Skeleton className="h-10 w-full mt-1.5" /> : (
              <select data-testid="booking-visit-type" value={vtId}
                      onChange={(e) => { setVtId(e.target.value); setConflict(false); setError(""); }}
                      className="mt-1.5 w-full border border-input bg-card rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring">
                {visitTypes.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} · {v.duration_min}m{v.booking_mode === "request" ? " (request type)" : ""}
                  </option>
                ))}
              </select>
            )}
            {vt?.booking_mode === "request" && (
              <p className="text-xs text-muted-foreground mt-1.5">
                Booked by staff, this confirms immediately — the request step only applies
                when a patient books it themselves.
              </p>
            )}
          </label>

          {/* Location — only when this visit type is genuinely offered at more than one */}
          {showLocationSelect && (
            <label className="block">
              <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Location</span>
              <select data-testid="booking-location" value={locChoice}
                      onChange={(e) => { setLocChoice(e.target.value); setChosen(null); }}
                      className="mt-1.5 w-full border border-input bg-card rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring">
                <option value="">Any location</option>
                {slotLocations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </label>
          )}

          {/* Day + time */}
          <label className="block">
            <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Day</span>
            <input type="date" data-testid="booking-date" value={day}
                   onChange={(e) => { setDay(e.target.value); setConflict(false); setError(""); }}
                   className="mt-1.5 w-full border border-input bg-card rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
          </label>

          <div>
            <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">
              Time {day && <span className="normal-case tracking-normal">· {dayLabel(day)}</span>}
            </span>
            {slots === null ? <Skeleton className="h-20 w-full mt-2" />
              : shownSlots.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-2" data-testid="booking-no-slots">
                  No openings for this visit type on this day. Try another day
                  {vt ? ` — ${vt.name} needs ${vt.duration_min} minutes.` : "."}
                </p>
              ) : (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="booking-slot-list">
                  {shownSlots.map((s) => {
                    const active = chosen?.start_utc === s.start_utc && chosen?.provider_id === s.provider_id;
                    return (
                      <button key={`${s.provider_id}-${s.start_utc}`}
                              data-testid={`booking-slot-${s.start_utc}`}
                              onClick={() => { setChosen(s); setError(""); }}
                              title={s.provider_name}
                              className={`px-3 py-2 rounded-md border text-sm transition-all ${
                                active ? "border-primary text-primary bg-primary/5 font-medium"
                                       : "border-border bg-card hover:border-primary hover:text-primary"}`}>
                        {timeLabel(s.start_utc, tz)}
                        <span className="block text-[10px] text-muted-foreground">
                          {s.provider_name}{multiLocation && s.location_name ? ` · ${s.location_name}` : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.05em] font-medium text-muted-foreground">Note (optional)</span>
            <textarea data-testid="booking-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                      className="mt-1.5 w-full border border-input bg-card rounded-md px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
          </label>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2"
               data-testid="booking-error">{error}</p>
          )}

          {/* Squeeze-ins: allowed, never silent. */}
          {conflict && allowOverride && (
            <div className="border border-amber-200 bg-amber-50 rounded-lg px-3 py-3" data-testid="booking-override">
              <p className="text-xs text-amber-900">
                Need this time anyway? Booking over a conflict is recorded against your
                name in the audit log.
              </p>
              <input data-testid="booking-override-reason" value={override}
                     onChange={(e) => setOverride(e.target.value)}
                     placeholder="Reason for the double-booking…"
                     className="mt-2 w-full border border-amber-300 bg-card rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring" />
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button data-testid="booking-confirm" onClick={confirm}
                    disabled={busy || !patient || !chosen || !vtId
                              || (conflict && !!override.trim() && override.trim().length < 5)}
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {conflict && override.trim() ? "Book over the conflict" : "Confirm booking"}
            </button>
            <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground px-2">
              Cancel
            </button>
          </div>

          {!patient && <p className="text-xs text-muted-foreground">Pick a patient to continue.</p>}
          {patient && !chosen && <p className="text-xs text-muted-foreground">Pick a time to continue.</p>}
        </div>
      </div>
    </div>
  );
}
